import { FC, Fragment, useState } from 'react'
import { Alert, Box, Button, Dialog, Divider, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { EmailMessageDisplay } from './EmailMessageDisplay'
import { EmailEditor } from './EmailEditor'
import { useCaseEmail } from '~/entities/case'
import { type ProvidersCasesEmailRetrieveResponse } from '@fireflyhealth/api'
import { Loader } from '~/shared/ui'
import { NAVY, YELLOW } from '~/shared/lib/theme'

interface EmailListViewProps {
  selectedCaseId: number
  onClose: () => void
}

const EmailList: FC<{
  subject: string
  linkedEmailContent: ProvidersCasesEmailRetrieveResponse['emails'][number][]
}> = props => {
  return (
    <>
      <Typography sx={{ px: 4, fontSize: 13, fontWeight: 'bold' }}>{props.subject}</Typography>
      {props.linkedEmailContent.map((email, i) => (
        <Fragment key={email.id}>
          <EmailMessageDisplay
            content={email.message}
            snippet={email.snippet ?? ''}
            to_address={email.to_address}
            from_address={email.from_address}
            send_at={email.email_sent_at ? email.email_sent_at : email.created_at}
            status={email.status ?? ''}
            attachments={email.attachments.map(att => ({
              fileName: att.file_name,
              attachmentId: att.attachment_id ?? '',
              presignedUrl: att.presigned_url,
              status: att.status ?? '',
              error: att.error ?? null,
            }))}
          />
          {/* Divider should not been shown to last email*/}
          {i < props.linkedEmailContent.length - 1 && <Divider variant="fullWidth" />}
        </Fragment>
      ))}
    </>
  )
}

export const EmailListView: FC<EmailListViewProps> = props => {
  const { result: caseEmailResponse, isLoading } = useCaseEmail(props.selectedCaseId)
  const emails = caseEmailResponse?.emails
  const providerMode = caseEmailResponse?.provider_mode === true
  const canSendEmail = caseEmailResponse?.can_send_email === true
  const reason = caseEmailResponse?.reason
  const [sendEmail, setSendEmail] = useState<boolean>(false)
  return (
    <Dialog
      open={true}
      onClose={(e: React.MouseEvent) => {
        e.stopPropagation()
        props.onClose()
      }}
      fullWidth={true}
      disableAutoFocus
      disableEnforceFocus
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', py: 3, flexGrow: 1 }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 4,
          }}
        >
          <Typography variant="h6">External Email</Typography>
          <IconButton color="inherit" onClick={props.onClose}>
            <CloseIcon color="inherit" />
          </IconButton>
        </Box>
        {providerMode && (
          <Box sx={{ px: 4, py: 2 }}>
            <Typography
              sx={theme => ({
                color: NAVY[500],
                fontSize: '13px',
                display: 'flex',
                flexDirection: 'row',
                padding: 2,
                width: '100%',
                backgroundColor: YELLOW[100],
              })}
            >
              Reminder: This is an email to the member&apos;s provider
            </Typography>
          </Box>
        )}

        {isLoading ? (
          <Loader />
        ) : (
          <>
            {emails?.[0] && <EmailList subject={emails[0].subject} linkedEmailContent={emails} />}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
                px: 4,
              }}
            >
              {!sendEmail && emails && emails.length > 0 && canSendEmail && (
                <Button
                  color="primary"
                  variant="contained"
                  sx={{
                    borderRadius: 10,
                    fontSize: '1.4rem',
                  }}
                  onClick={() => setSendEmail(true)}
                >
                  {providerMode ? 'Reply to Provider' : 'Reply'}
                </Button>
              )}
              {props.selectedCaseId &&
                canSendEmail &&
                (sendEmail || !emails || emails.length === 0) && (
                  <EmailEditor
                    content={''}
                    case_id={props.selectedCaseId}
                    onCancel={() => setSendEmail(false)}
                    submitLabel={providerMode ? 'Submit to Provider' : 'Submit'}
                  />
                )}
            </Box>
            {!canSendEmail && reason && (
              <Box sx={{ px: 4, pt: 2 }}>
                <Alert severity="error">{reason}</Alert>
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  )
}
